import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../components/auth';
import * as apiClient from '../lib/api';
import { API_URL } from '../lib/api';
import { VIDEOS } from '../lib/media';
import HeroVideo from '../components/HeroVideo';

export default function Login() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Redirect to returnTo path if present, otherwise dashboard
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, from]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedName = firstName.trim();
    if (!trimmedEmail || !trimmedEmail.includes('@')) return;
    if (!trimmedName) return;
    setBusy(true);
    setError('');
    try {
      await apiClient.auth.sendMagicLink(trimmedEmail, trimmedName);
      setSent(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Try again.');
    }
    setBusy(false);
  }

  return (
    <div className="login-shell">
      {/* LEFT - Dark brand panel */}
      <div className="login-brand">
        {/* Background video */}
        <HeroVideo className="login-bg-video" src={VIDEOS.loginBg} preload="auto" />

        {/* Dark tint overlay */}
        <div className="login-tint-overlay" />

        {/* Warm red glow - top right */}
        <div className="login-glow login-glow--top-right" />
        {/* Warm red glow - bottom left */}
        <div className="login-glow login-glow--bottom-left" />

        {/* Top - logo */}
        <div className="login-logo-wrap">
          <Link to="/" className="login-logo-link">
            <span className="login-logo-text">
              Fasting <span className="login-logo-accent">Carnivore</span>
            </span>
          </Link>
        </div>

        {/* Middle - hero copy, centered */}
        <div className="login-hero-copy">
          <h2 className="login-hero-headline">
            Eat with<br />intention.<br />
            <span className="login-hero-highlight">Live with power.</span>
          </h2>

          <div className="login-features">
            {[
              'Personalized Fasting Protocols',
              'Carnivore Meal Planning',
              'Metabolic Tracking & Insights',
              'Four Books & Workbooks',
              'Private Community Access',
            ].map((item) => (
              <span key={item} className="login-feature-item">
                <span className="login-feature-bullet">&bull;</span>
                {item}
              </span>
            ))}
          </div>
        </div>

        {/* Bottom - words */}
        <div className="login-bottom-words">
          <span>Fasting</span>
          <span className="login-bottom-dot">&middot;</span>
          <span>Carnivore</span>
          <span className="login-bottom-dot">&middot;</span>
          <span>Performance</span>
        </div>
      </div>

      {/* RIGHT - Auth form */}
      <div className="login-form-side">
        <div className="login-form-container">
          {!sent ? (
            <>
              {/* Back link */}
              <div className="login-back-wrap">
                <Link to="/" className="login-back-link">
                  &larr; Back to Site
                </Link>
              </div>

              <p className="login-eyebrow">Sign In</p>

              <h2 className="login-welcome">
                Welcome.
              </h2>

              <p className="login-subtitle">
                Enter your email to receive a sign-in link.
              </p>

              {error && (
                <div className="login-error">{error}</div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="login-field">
                  <label className="login-label">
                    First Name
                  </label>
                  <input
                    type="text"
                    required
                    autoFocus
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    placeholder="Your first name"
                    className="login-input"
                  />
                </div>

                <div className="login-field">
                  <label className="login-label">
                    Email Address
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="login-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={busy}
                  className={`login-submit${busy ? ' login-submit--busy' : ''}`}
                >
                  {busy ? 'Sending...' : 'Send Sign-In Link'}
                </button>
              </form>

              <div className="login-divider" />

              <p className="login-or-text" style={{ textAlign: 'center', fontSize: '13px', color: '#94a3b8', margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                or sign in with
              </p>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <a
                  href={`${API_URL}/api/auth/discord`}
                  className="login-discord-icon"
                  title="Sign in with Discord"
                >
                  <svg width="24" height="18" viewBox="0 0 71 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M60.1 4.9A58.5 58.5 0 0045.4.2a.2.2 0 00-.2.1 40.7 40.7 0 00-1.8 3.7 54 54 0 00-16.2 0A26.4 26.4 0 0025.4.3a.2.2 0 00-.2-.1A58.3 58.3 0 0010.5 4.9a.2.2 0 00-.1.1C1.5 18.7-.9 32.2.3 45.5v.1a58.7 58.7 0 0017.7 9a.2.2 0 00.3-.1 42 42 0 003.6-5.9.2.2 0 00-.1-.3 38.7 38.7 0 01-5.5-2.6.2.2 0 01 0-.4c.4-.3.7-.6 1.1-.8a.2.2 0 01.2 0c11.6 5.3 24.1 5.3 35.5 0a.2.2 0 01.3 0l1 .9a.2.2 0 010 .3c-1.7 1-3.6 1.9-5.5 2.6a.2.2 0 00-.1.4 47.2 47.2 0 003.7 5.9.2.2 0 00.2.1 58.5 58.5 0 0017.7-9v-.1c1.4-15-2.3-28.1-9.8-39.7a.2.2 0 00-.1-.1zM23.7 37.3c-3.4 0-6.3-3.2-6.3-7s2.8-7 6.3-7 6.4 3.2 6.3 7-2.8 7-6.3 7zm23.2 0c-3.4 0-6.3-3.2-6.3-7s2.8-7 6.3-7 6.4 3.2 6.3 7-2.8 7-6.3 7z" fill="currentColor"/>
                  </svg>
                </a>
              </div>

              <p className="login-footnote">
                No password needed. We'll email you a secure link that logs you in instantly.
              </p>
            </>
          ) : (
            /* Sent confirmation */
            <div className="login-sent">
              <div className="login-sent-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
              </div>
              <h2 className="login-sent-title">
                Check your email
              </h2>
              <p className="login-sent-desc">
                We sent a sign-in link to
              </p>
              <p className="login-sent-email">{email}</p>
              <p className="login-sent-expire">
                Click the link in the email to access your account. It expires in 15 minutes.
              </p>
              <button
                type="button"
                onClick={() => { setSent(false); setFirstName(''); setEmail(''); setError(''); }}
                className="login-retry-btn"
              >
                Use a different email
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Verification page - handles magic link callback at /auth/verify?token=xxx
 */
export function AuthVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSessionToken } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(true);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setError('No verification token found.');
      setVerifying(false);
      return;
    }

    let cancelled = false;

    async function verify() {
      try {
        const data = await apiClient.auth.verify(token!);
        if (cancelled) return;
        if (data.token) {
          setSessionToken(data.token);
          // If this is a first-time buyer coming from the purchase flow, send to /success
          const justPurchased = sessionStorage.getItem('fc_just_purchased');
          if (justPurchased) {
            sessionStorage.removeItem('fc_just_purchased');
            navigate('/success', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        }
      } catch (err: any) {
        if (cancelled) return;
        setError(err.message || 'Verification failed. The link may have expired.');
        setVerifying(false);
      }
    }

    verify();
    return () => { cancelled = true; };
  }, [searchParams, navigate, setSessionToken]);

  return (
    <div className="login-verify">
      <div className="login-verify-inner">
        {verifying && !error ? (
          <>
            <p className="login-verify-eyebrow">Verifying</p>
            <h1 className="login-verify-title">Signing you in...</h1>
            <p className="login-verify-desc">Please wait a moment.</p>
          </>
        ) : (
          <>
            <p className="login-verify-eyebrow login-verify-eyebrow--error">Error</p>
            <h1 className="login-verify-title">Link expired.</h1>
            <p className="login-verify-desc login-verify-desc--mb">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="login-verify-retry"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Discord OAuth callback — handles /auth/discord?token=xxx
 */
export function DiscordCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSessionToken } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');
    const err = searchParams.get('error');

    if (err) {
      const messages: Record<string, string> = {
        discord_denied: 'Discord login was cancelled.',
        discord_token_failed: 'Failed to authenticate with Discord. Try again.',
        discord_user_failed: 'Could not retrieve your Discord profile.',
        discord_no_email: 'Your Discord account does not have a verified email. Please add one in Discord settings.',
        no_membership: 'No active membership found. Please purchase access first.',
        membership_inactive: 'Your membership is no longer active.',
      };
      setError(messages[err] ?? 'Something went wrong.');
      return;
    }

    if (!token) {
      setError('No session token received.');
      return;
    }

    setSessionToken(token);
    navigate('/dashboard', { replace: true });
  }, [searchParams, navigate, setSessionToken]);

  if (!error) {
    return (
      <div className="login-verify">
        <div className="login-verify-inner">
          <p className="login-verify-eyebrow">Discord</p>
          <h1 className="login-verify-title">Signing you in...</h1>
          <p className="login-verify-desc">Please wait a moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-verify">
      <div className="login-verify-inner">
        <p className="login-verify-eyebrow login-verify-eyebrow--error">Error</p>
        <h1 className="login-verify-title">Login failed.</h1>
        <p className="login-verify-desc login-verify-desc--mb">{error}</p>
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="login-verify-retry"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}
